import { Injectable } from '@angular/core'
import { CaseSummary } from '@ftr/annotations-contracts/v2'
import { AnnotationsApiClientFactory, CasesService } from '@ftr/api-annotations'
import { Uuid } from '@ftr/contracts/type/shared'
import { CourtSystemRegionCache } from '@ftr/data-court-system'
import { RemoteData, ToastService } from '@ftr/foundation'
import { LocalDate } from '@js-joda/core'
import { Action, createSelector, Selector, State, StateContext, StateOperator } from '@ngxs/store'
import { patch } from '@ngxs/store/operators'
import { memoize, uniq } from 'lodash-es'
import { filter, firstValueFrom, Observable, of } from 'rxjs'
import { UpcomingHearingWithSummary } from '../lib'
import {
  FetchCaseSummariesCommand,
  FetchNextUpcomingHearingsPageCommand,
  FetchUpcomingHearingsPageCommand,
  InitialiseUpcomingHearingsListCommand,
  SetOngoingCaseReferenceCommand,
  UpdateCaseSummaryVisibilityCommand,
} from './upcoming-hearings-list.commands'
import { UpcomingHearingsListAccessForbidden } from './upcoming-hearings-list.events'
import {
  PageResult,
  UpcomingHearingsListInstanceStateModel,
  UpcomingHearingsListStateModel,
} from './upcoming-hearings-list.model'

export const PAGE_SIZE = 10

export function defaultUpcomingHearingsListState(): UpcomingHearingsListStateModel {
  return { instances: {} }
}

@State<UpcomingHearingsListStateModel>({
  name: 'upcomingHearingListState',
  defaults: defaultUpcomingHearingsListState(),
})
@Injectable()
export class UpcomingHearingsListState {
  constructor(
    private readonly regionCache: CourtSystemRegionCache,
    private readonly annotationsApiFactory: AnnotationsApiClientFactory,
    private readonly casesService: CasesService,
    private readonly toastService: ToastService,
  ) {}

  @Selector()
  static allInstanceStates(
    state: UpcomingHearingsListStateModel,
  ): Record<Uuid, UpcomingHearingsListInstanceStateModel> {
    return state?.instances ?? {}
  }

  static readonly upcomingHearings = memoize(
    (
      courtroomId: Uuid,
      date: LocalDate,
    ): ((
      sourceStates: ReturnType<typeof UpcomingHearingsListState.allInstanceStates>,
    ) => UpcomingHearingWithSummary[]) => {
      return createSelector([UpcomingHearingsListState.allInstanceStates], sourceStates => {
        const instance = sourceStates[getInstanceId(courtroomId, date)]
        return instance?.upcomingHearings ?? []
      })
    },
    (courtroomId, date) => getInstanceId(courtroomId, date),
  )

  static readonly ongoingCaseReference = memoize(
    (
      courtroomId: Uuid,
      date: LocalDate,
    ): ((sourceStates: ReturnType<typeof UpcomingHearingsListState.allInstanceStates>) => string | undefined) => {
      return createSelector([UpcomingHearingsListState.allInstanceStates], sourceStates => {
        const instance = sourceStates[getInstanceId(courtroomId, date)]
        return instance?.ongoingCaseReference
      })
    },
    (courtroomId, date) => getInstanceId(courtroomId, date),
  )

  static readonly pageResultRemote = memoize(
    (
      courtroomId: Uuid,
      date: LocalDate,
    ): ((sourceStates: ReturnType<typeof UpcomingHearingsListState.allInstanceStates>) => RemoteData<PageResult>) => {
      return createSelector([UpcomingHearingsListState.allInstanceStates], sourceStates => {
        const instance = sourceStates[getInstanceId(courtroomId, date)]
        return instance?.pageResultRemote ?? RemoteData.notAsked()
      })
    },
    (courtroomId, date) => getInstanceId(courtroomId, date),
  )

  @Action(InitialiseUpcomingHearingsListCommand)
  private initialiseUpcomingHearingsList(
    { setState, dispatch }: StateContext<UpcomingHearingsListStateModel>,
    { courtSystemId, courtroomId, date, searchTerm, fetchCaseSummaries }: InitialiseUpcomingHearingsListCommand,
  ): Observable<void> {
    const instanceId = getInstanceId(courtroomId, date)
    setState(
      patch({
        instances: patch({
          [instanceId]: {
            courtSystemId,
            courtroomId,
            date,
            upcomingHearings: [],
            pageResultRemote: RemoteData.notAsked(),
            searchTerm,
            ongoingCaseReference: undefined,
            fetchCaseSummaries,
          },
        }),
      }),
    )

    return dispatch(new FetchUpcomingHearingsPageCommand(instanceId, 0, PAGE_SIZE))
  }

  @Action(FetchUpcomingHearingsPageCommand)
  private async fetchUpcomingHearingsPage(
    { getState, setState, dispatch }: StateContext<UpcomingHearingsListStateModel>,
    { instanceId, offset, limit }: FetchUpcomingHearingsPageCommand,
  ): Promise<void> {
    const { courtSystemId, courtroomId, date, pageResultRemote, searchTerm, fetchCaseSummaries } =
      getState().instances[instanceId]

    if (pageResultRemote.isLoading()) {
      return
    }

    setState(
      makeInstancePatch(instanceId, {
        pageResultRemote: RemoteData.loading(),
      }),
    )

    const apiClient = this.annotationsApiFactory.build(
      (await firstValueFrom(this.regionCache.getOrFetchCourtSystem(of(courtSystemId)))).region,
    )

    try {
      const result = await apiClient.upcomingHearings.findUpcomingHearings({
        query: {
          courtSystemId,
          locationId: courtroomId,
          fromDate: date.toString(),
          toDate: date.toString(),
          offset,
          limit,
          searchTerm,
        },
      })

      // short circuit if the search term changes before the request completed
      if (getState().instances[instanceId].searchTerm !== searchTerm) {
        return
      }

      if (result.status === 200) {
        const currentHearings = getState().instances[instanceId].upcomingHearings
        const existingSummariesByCaseId = new Map(
          currentHearings
            .filter(hearing => hearing.caseSummary !== undefined)
            .map(hearing => [hearing.caseId, hearing.caseSummary] as const),
        )
        const pageHearings: UpcomingHearingWithSummary[] = result.body.items.map(item => ({
          ...item,
          caseSummary: existingSummariesByCaseId.get(item.caseId),
        }))

        setState(
          makeInstancePatch(instanceId, {
            upcomingHearings: currentHearings.concat(pageHearings),
            pageResultRemote: RemoteData.success({ hasMore: result.body.items.length === limit }),
          }),
        )

        // we don't expect the same caseId to appear in multiple upcoming hearings on a day,
        // but deduping costs nothing and avoids redundant requests if that ever changes.
        const caseIdsNeedingSummary = uniq(
          pageHearings.filter(hearing => hearing.caseSummary === undefined).map(hearing => hearing.caseId),
        )
        if (fetchCaseSummaries && caseIdsNeedingSummary.length) {
          dispatch(new FetchCaseSummariesCommand(instanceId, courtSystemId, caseIdsNeedingSummary))
        }
      } else {
        setState(
          makeInstancePatch(instanceId, {
            pageResultRemote: RemoteData.failure(
              new Error('Failed to fetch upcoming hearings', { cause: result.body }),
            ),
          }),
        )
        if (result.status === 403) {
          dispatch(new UpcomingHearingsListAccessForbidden(courtroomId))
        }
      }
    } catch (err) {
      setState(
        makeInstancePatch(instanceId, {
          pageResultRemote: RemoteData.failure(new Error('Failed to fetch upcoming hearings', { cause: err })),
        }),
      )
    }
  }

  @Action(FetchNextUpcomingHearingsPageCommand)
  private fetchNextUpcomingHearingsPage(
    { getState, dispatch }: StateContext<UpcomingHearingsListStateModel>,
    { courtroomId, date }: FetchNextUpcomingHearingsPageCommand,
  ): Observable<void> {
    const instanceId = getInstanceId(courtroomId, date)
    const { upcomingHearings } = getState().instances[instanceId]

    const offset = upcomingHearings.length
    return dispatch(new FetchUpcomingHearingsPageCommand(instanceId, offset, PAGE_SIZE))
  }

  @Action(SetOngoingCaseReferenceCommand)
  private setOngoingCaseReference(
    { setState }: StateContext<UpcomingHearingsListStateModel>,
    { courtroomId, date, caseReference }: SetOngoingCaseReferenceCommand,
  ): void {
    const instanceId = getInstanceId(courtroomId, date)
    setState(
      makeInstancePatch(instanceId, {
        ongoingCaseReference: caseReference,
      }),
    )
  }

  @Action(FetchCaseSummariesCommand)
  private async fetchCaseSummaries(
    { getState, setState }: StateContext<UpcomingHearingsListStateModel>,
    { instanceId, courtSystemId, caseIds }: FetchCaseSummariesCommand,
  ): Promise<void> {
    const instance = getState().instances[instanceId]
    if (!instance?.fetchCaseSummaries || !caseIds.length) {
      return
    }

    await Promise.all(
      caseIds.map(async caseId => {
        const result = await firstValueFrom(
          this.casesService.getCaseSummary({ caseId, courtSystemId }).pipe(filter(data => data.isCompleted())),
        )

        if (result.isSuccess() && result._data) {
          const currentHearings = getState().instances[instanceId]?.upcomingHearings ?? []
          setState(patchUpcomingHearingsCaseSummary(currentHearings, instanceId, caseId, result._data))
        }
      }),
    )
  }

  @Action(UpdateCaseSummaryVisibilityCommand)
  private async updateCaseSummaryVisibility(
    { getState, setState }: StateContext<UpcomingHearingsListStateModel>,
    { courtroomId, date, courtSystemId, caseId, isHidden }: UpdateCaseSummaryVisibilityCommand,
  ): Promise<void> {
    const result = await firstValueFrom(
      this.casesService
        .updateCaseSummary({ courtSystemId, caseId }, { isHidden })
        .pipe(filter(data => data.isCompleted())),
    )

    if (result.isSuccess()) {
      const instanceId = getInstanceId(courtroomId, date)
      const instance = getState().instances[instanceId]
      if (!instance) {
        return
      }

      setState(patchUpcomingHearingsCaseSummary(instance.upcomingHearings, instanceId, caseId, result._data))
    } else {
      this.toastService.error('Failed to update summary')
    }
  }
}

function patchUpcomingHearingsCaseSummary(
  upcomingHearings: UpcomingHearingWithSummary[],
  instanceId: string,
  caseId: Uuid,
  caseSummary: CaseSummary,
): StateOperator<UpcomingHearingsListStateModel> {
  return makeInstancePatch(instanceId, {
    upcomingHearings: upcomingHearings.map(hearing =>
      hearing.caseId === caseId ? { ...hearing, caseSummary } : hearing,
    ),
  })
}

function makeInstancePatch(
  instanceId: string,
  instancePartial: Partial<UpcomingHearingsListInstanceStateModel>,
): StateOperator<UpcomingHearingsListStateModel> {
  return patch({
    instances: patch({
      [instanceId]: patch(instancePartial),
    }),
  })
}

export function getInstanceId(courtroomId: Uuid, date: LocalDate): string {
  return `courtroom:${courtroomId}_date:${date.toString()}`
}
